import React, { useEffect, useState } from 'react';
import {
  getProductEnquiries,
  updateProductEnquiry,
  deleteProductEnquiry,
} from '../../api';
import './ProductEnquiries.css';
import { FaEye, FaTrash, FaSearch } from 'react-icons/fa';

const ProductEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState('');
  const [openCardId, setOpenCardId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [viewAll, setViewAll] = useState(false);
  const PER_PAGE = 10;

  const fetchData = async () => {
    try {
      const data = await getProductEnquiries();
      if (Array.isArray(data)) setEnquiries(data);
    } catch (err) {
      console.error('Fetch error:', err);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const products = ['all', ...new Set(enquiries.map((e) => e.product || 'Other'))];

  const filtered = enquiries
    .filter((e) => (filter === 'all' ? true : (e.product || 'Other') === filter))
    .filter((e) => {
      const q = search.toLowerCase();
      return (
        e.name?.toLowerCase().includes(q) ||
        e.email?.toLowerCase().includes(q) ||
        e.phone?.includes(q) ||
        e.message?.toLowerCase().includes(q)
      );
    });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const startIdx = (currentPage - 1) * PER_PAGE;
  const displayed = viewAll ? filtered : filtered.slice(startIdx, startIdx + PER_PAGE);

  useEffect(() => { setCurrentPage(1); }, [filter, search]);

  const stats = {
    total: enquiries.length,
    unread: enquiries.filter((e) => !e.is_read).length,
    replied: enquiries.filter((e) => e.is_replied).length,
    pending: enquiries.filter((e) => !e.is_replied).length,
  };

  const handleReplyWhatsApp = (c) => {
    const phone = c.phone.replace(/\D/g, '');
    const cleanPhone = phone.length === 10 ? `91${phone}` : phone;
    const msg = encodeURIComponent(
      `Hello ${c.name}, thank you for contacting YashviTech regarding "${c.product}". Our team will get back to you shortly.`
    );
    window.open(`https://wa.me/${cleanPhone}?text=${msg}`, '_blank');
  };

  const handleView = async (c) => {
    setSelected(c);
    if (!c.is_read) {
      try { await updateProductEnquiry(c.id, { is_read: true }); fetchData(); }
      catch (err) { console.error(err); }
    }
  };

  const handleMarkReplied = async (id) => {
    try { await updateProductEnquiry(id, { is_replied: true }); fetchData(); }
    catch (err) { console.error(err); }
  };

  const confirmDelete = async () => {
    try { await deleteProductEnquiry(deleteId); fetchData(); }
    catch (err) { console.error(err); }
    finally { setDeleteId(null); }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div className="pe-container">
      <h1>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
        Product Enquiries
      </h1>
      <p className="pe-subtitle">All product inquiries in one place</p>

      {/* Stats */}
      <div className="pe-stats">
        <div className="pe-stat-card"><span>▣</span><div><p>Total</p><h3>{stats.total}</h3></div></div>
        <div className="pe-stat-card"><span>✉</span><div><p>Unread</p><h3>{stats.unread}</h3></div></div>
        <div className="pe-stat-card"><span>✓</span><div><p>Replied</p><h3>{stats.replied}</h3></div></div>
        <div className="pe-stat-card"><span>◷</span><div><p>Pending</p><h3>{stats.pending}</h3></div></div>
      </div>

      {/* Search + Filter */}
      <div className="pe-toolbar">
        <div className="pe-search">
          <FaSearch />
          <input
            type="text"
            placeholder="Search name, email, phone, message..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select className="pe-filter-select" value={filter} onChange={(e) => setFilter(e.target.value)}>
          {products.map((p) => (
            <option key={p} value={p}>{p === 'all' ? 'All Products' : p}</option>
          ))}
        </select>
      </div>

      {/* Desktop Table */}
      <div className="pe-table-section pe-desktop">
        <table className="pe-table">
          <thead>
            <tr>
              <th>S.NO</th>
              <th>PRODUCT</th>
              <th>NAME</th>
              <th>EMAIL</th>
              <th>PHONE</th>
              <th>MESSAGE</th>
              <th>DATE</th>
              <th>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {displayed.length === 0 ? (
              <tr><td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>No enquiries found</td></tr>
            ) : (
              displayed.map((c, idx) => (
                <tr key={c.id}>
                  <td>{startIdx + idx + 1}</td>
                  <td><span className="pe-product-badge">{c.product || 'Other'}</span></td>
                  <td title={c.name}>{c.name}{!c.is_read && <span className="pe-new">NEW</span>}</td>
                  <td title={c.email}>{c.email}</td>
                  <td>{c.phone}</td>
                  <td title={c.message}>{c.message}</td>
                  <td>{formatDate(c.created_at)}</td>
                  <td className="pe-actions">
                    <div className="pe-actions-inner">
                      <button className="pe-btn view" onClick={() => handleView(c)}><FaEye /></button>
                      <button className="pe-btn del" onClick={() => setDeleteId(c.id)}><FaTrash /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="pe-mobile-cards">
        {displayed.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '20px' }}>No enquiries found</p>
        ) : (
          displayed.map((c) => {
            const isOpen = openCardId === c.id;
            return (
              <div key={c.id} className="pe-mobile-card">
                <div className="pe-mobile-card-header" onClick={() => setOpenCardId(isOpen ? null : c.id)}>
                  <div className="pe-mobile-card-title">
                    <strong>{c.name}</strong>
                    {!c.is_read && <span className="pe-new">NEW</span>}
                  </div>
                  <div className="pe-mobile-card-right">
                    <span className="pe-mobile-date">{formatDate(c.created_at)}</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>
                {isOpen && (
                  <div className="pe-mobile-card-body">
                    <p><b>Product:</b> <span className="pe-product-badge">{c.product || 'Other'}</span></p>
                    <p><b>Email:</b> {c.email}</p>
                    <p><b>Phone:</b> {c.phone}</p>
                    <p><b>Message:</b> {c.message}</p>
                    <div className="pe-mobile-actions">
                      <button className="pe-btn view" onClick={() => handleView(c)}><FaEye /></button>
                      <button className="pe-btn del" onClick={() => setDeleteId(c.id)}><FaTrash /></button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {!viewAll && totalPages > 1 && (
        <div className="pe-pagination">
          <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}>←</button>
          <span>{currentPage} / {totalPages}</span>
          <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>→</button>
          <button className="view-all" onClick={() => setViewAll(true)}>View All →</button>
        </div>
      )}
      {viewAll && (
        <div className="pe-pagination">
          <button className="view-all" onClick={() => { setViewAll(false); setCurrentPage(1); }}>Show Less</button>
        </div>
      )}

      {/* View Modal */}
      {selected && (
        <div className="pe-modal-overlay" onClick={() => setSelected(null)}>
          <div className="pe-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{selected.name}</h3>
            <p className="pe-modal-product">{selected.product || 'Other'}</p>
            <p><b>Email:</b> {selected.email}</p>
            <p><b>Phone:</b> {selected.phone}</p>
            {selected.company && <p><b>Company:</b> {selected.company}</p>}
            <p><b>Date:</b> {formatDate(selected.created_at)}</p>
            <p><b>Status:</b> {selected.is_replied ? 'Replied' : 'Pending'}</p>
            <div className="pe-modal-msg">
              <b>Message:</b>
              <p>{selected.message || 'No message'}</p>
            </div>
            <div className="pe-modal-actions">
              <button className="pe-btn whatsapp" onClick={() => handleReplyWhatsApp(selected)}>Reply on WhatsApp</button>
              {!selected.is_replied && (
                <button className="pe-btn mark" onClick={() => { handleMarkReplied(selected.id); setSelected(null); }}>Mark as Replied</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteId && (
        <div className="pe-modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="pe-modal pe-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Enquiry?</h3>
            <p>This action cannot be undone.</p>
            <div className="pe-modal-actions">
              <button className="pe-btn cancel" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="pe-btn del" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductEnquiries;